import { useState, useRef, useMemo, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export interface CategoryComboboxProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Autocomplete category picker for the admin side. Type to filter existing categories and
 * pick one, or type a brand-new name to add it directly — no approval needed. The current
 * query is committed on Enter or by clicking an option / the "Add" affordance.
 *
 * Uses a plain inline dropdown rather than a portal/Popover so focus stays on the input
 * while typing (Radix's focus-scoping blurs the field and drops keystrokes mid-edit).
 */
export function CategoryCombobox({
  options,
  value,
  onChange,
  placeholder = "Select or add a category",
  id,
  disabled = false,
  className,
}: CategoryComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const editingRef = useRef(false);

  // Adopt external value changes only when the user isn't actively editing.
  useEffect(() => {
    if (!editingRef.current) setQuery(value);
  }, [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const queryMatchesOption = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.some((o) => o.toLowerCase() === q);
  }, [options, query]);

  const commit = (next: string) => {
    editingRef.current = false;
    onChange(next);
    setQuery(next);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter") {
      const q = query.trim();
      if (!q) return;
      e.preventDefault();
      // Prefer an exact existing match (case-insensitive); otherwise add the typed value.
      const exact = options.find((o) => o.toLowerCase() === q.toLowerCase());
      commit(exact ?? q);
    }
  };

  const showAdd = query.trim().length > 0 && !queryMatchesOption;

  return (
    <div className={cn("relative", className)}>
      <div
        className={cn(
          "flex h-9 w-full items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm ring-offset-background max-md:text-base!",
          "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <input
          id={id}
          role="combobox"
          aria-expanded={open}
          value={query}
          onChange={(e) => {
            editingRef.current = true;
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          // Delay close so an option's click (which blurs the input) still registers.
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        />
        <ChevronDown className="pointer-events-none size-4 shrink-0 opacity-50" />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
          <div className="max-h-[240px] overflow-y-auto overscroll-contain">
            {filtered.map((option) => (
              <button
                key={option}
                type="button"
                // Commit before blur so the click isn't swallowed by the blur-close.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(option)}
                className={cn(
                  "flex w-full items-center rounded-sm px-2 py-1.5 text-sm",
                  "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground outline-none",
                )}
              >
                {option}
              </button>
            ))}
            {showAdd && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(query.trim())}
                className={cn(
                  "flex w-full items-center rounded-sm px-2 py-1.5 text-sm italic text-muted-foreground",
                  "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground outline-none",
                )}
              >
                Add &ldquo;{query.trim()}&rdquo;
              </button>
            )}
            {filtered.length === 0 && !showAdd && (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                No categories available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
